import * as THREE from 'three';
import { BASE_ASSET_URL } from './ttGlbLoader';

export class ttMetashapeSensor {
    label: string | null = null;
    id: number | null = null;
    widthPx: number = 1;
    heightPx: number = 1;
    f: number = 0.0;
    cx: number = 0.0;
    cy: number = 0.0;
    k1: number = 0.0;
    k2: number = 0.0;
    k3: number = 0.0;
    k4: number = 0.0;
    p1: number = 0.0;
    p2: number = 0.0;

    prop_pixel_width: number = 0.0;
    prop_pixel_height: number = 0.0;
    prop_focal_length: number = 0.0;
}

export interface ttMetashapeCamera {
    label: string;
    transform: THREE.Matrix4;
    worldToCamera: THREE.Matrix4;
    sensorId: number;
    componentId: number;
    hasReference: boolean;
    orientation: number;
}

export class ttMetashapeData {
    label: string;
    transform: THREE.Matrix4;
    scale: number;
    cameras: ttMetashapeCamera[];
    sensors: ttMetashapeSensor[];

    constructor(label: string, transform: THREE.Matrix4, scale: number,
                cameras: ttMetashapeCamera[],
                sensors: ttMetashapeSensor[]) {
        this.label      = label;
        this.transform  = transform;
        this.scale      = scale;
        this.cameras    = cameras;
        this.sensors    = sensors;
    };
};

function convertStr(value: string | null) {
    if (value == null)
        return false;
    if (value.toLowerCase() === "true")
        return true;
    if (value.toLowerCase() === "false")
        return false;
    if (!isNaN(Number(value))) {
        if (Number.isInteger(parseFloat(value)))
            return parseInt(value, 10);
        return parseFloat(value);
    }
    return value;
}

/**
 * @returns {ttMetashapeData}
 */

export interface MetashapeParseOptions {
    useCalibratedCameraTransforms?: boolean;
    repairLegacyXml?: boolean;
}

function repairLegacyMetashapeXml(source: string): string {
    const cameraGroupOpenings: RegExpExecArray[] = [];
    const openingPattern = /<cameras\b[^>]*>/gi;
    let opening: RegExpExecArray | null;
    while ((opening = openingPattern.exec(source)) !== null) cameraGroupOpenings.push(opening);
    const cameraGroupClosingCount = (source.match(/<\/cameras\s*>/gi) ?? []).length;

    // Some older Metashape exports contain a second <cameras> immediately
    // after the final </camera>, where the closing </cameras> tag belongs.
    if (cameraGroupOpenings.length === 2 && cameraGroupClosingCount === 0) {
        const misplacedOpening = cameraGroupOpenings[1];
        const index = misplacedOpening.index;
        if (index !== undefined && source.lastIndexOf('</camera>', index) >= 0) {
            return source.slice(0, index) + '</cameras>' + source.slice(index + misplacedOpening[0].length);
        }
    }

    return source;
}

export async function ttMetashapeParseUrl(
    url: string,
    signal?: AbortSignal,
    options: MetashapeParseOptions = {},
) : Promise<ttMetashapeData> {
    const resp = await fetch(url, { signal });
    if (!resp.ok) {
        throw new Error(`Failed to load Metashape XML ${url}: HTTP ${resp.status} ${resp.statusText}`);
    }
    const str = await resp.text();
    const xmlSource = options.repairLegacyXml ? repairLegacyMetashapeXml(str) : str;
    if (xmlSource !== str) {
        console.warn(`Repaired malformed legacy Metashape camera-group closing tag in ${url}.`);
    }

    const parser = new DOMParser();
    const xml = parser.parseFromString(xmlSource, "text/xml");
    const parserError = xml.querySelector('parsererror');
    if (parserError) {
        throw new Error(`Invalid Metashape XML ${url}: ${parserError.textContent?.trim() || 'parser error'}`);
    }

    const components = xml.getElementsByTagName('components')[0];
    const component = components ? components.getElementsByTagName("component")[0] : null;
    if (!component) {
        throw new Error(`Invalid Metashape XML ${url}: missing component information.`);
    }
    const msComponentTransform = component ? component.getElementsByTagName("transform")[0] : null;

    const translationEl = msComponentTransform ? msComponentTransform.getElementsByTagName("translation")[0] : null;
    const rotationEl = msComponentTransform ? msComponentTransform.getElementsByTagName("rotation")[0] : null;
    const scaleEl = msComponentTransform ? msComponentTransform.getElementsByTagName("scale")[0] : null;

    const msOrigin = translationEl && translationEl.textContent
        ? translationEl.textContent.split(' ').map(item => parseFloat(item))
        : [0.0, 0.0, 0.0];

    const msOriginRotation = rotationEl && rotationEl.textContent
        ? rotationEl.textContent.split(' ').map(item => parseFloat(item))
        : [1.0,0.0,0.0, 0.0,1.0,0.0, 0.0,0.0,1.0];

    const msScale = scaleEl && scaleEl.textContent ? parseFloat(scaleEl.textContent) : 1.0;

    let msOriginRotationTfx = new THREE.Matrix3();
    msOriginRotationTfx.elements = msOriginRotation;
    msOriginRotationTfx.transpose();

    let msTfx = new THREE.Matrix4();
    msTfx.setFromMatrix3(msOriginRotationTfx);
    msTfx.setPosition(new THREE.Vector3(msOrigin[0], msOrigin[1], msOrigin[2]));

    const msCamerasParent = xml.getElementsByTagName("cameras")[0];
    const msCameras = msCamerasParent ? msCamerasParent.children : [];
    if (!msCamerasParent || msCameras.length === 0) {
        throw new Error(`Invalid Metashape XML ${url}: no cameras found.`);
    }
    const msCameraData: ttMetashapeCamera[] = [];
    let hasReference = false;
    for (let msCamera of msCameras as any) {
        let tfx = new THREE.Matrix4();
        const transformEl = msCamera.getElementsByTagName("transform")[0];
        if (transformEl && transformEl.textContent) {
            tfx.elements = transformEl.textContent.split(' ').map((item: string) => parseFloat(item));
            tfx.transpose();
            tfx.elements[12] = (tfx.elements[12] || 0.0) * msScale;
            tfx.elements[13] = (tfx.elements[13] || 0.0) * msScale;
            tfx.elements[14] = (tfx.elements[14] || 0.0) * msScale;
        }
        const reference = msCamera.getElementsByTagName('reference')[0] || null;
        if (reference) {
            hasReference = true;
        }
        if (reference && !options.useCalibratedCameraTransforms) {
            tfx.identity();
            const refX = reference.getAttribute('x');
            const refY = reference.getAttribute('y');
            const refZ = reference.getAttribute('z');
            const refYaw = reference.getAttribute('yaw');
            const refPitch = reference.getAttribute('pitch');
            const refRoll = reference.getAttribute('roll');
            const refRotEnabled = reference.getAttribute('rotation_enabled');
            if (refRotEnabled ? refRotEnabled === 'true' : true) {
                const yaw = THREE.MathUtils.degToRad(refYaw ? parseFloat(refYaw) : 0.0);
                const pitch = THREE.MathUtils.degToRad(refPitch ? parseFloat(refPitch) : 0.0);
                const roll = THREE.MathUtils.degToRad(refRoll ? parseFloat(refRoll) : 0.0);
                tfx.makeRotationFromEuler(new THREE.Euler(roll, pitch, yaw, 'ZYX'));
            }
            tfx.elements[12] = refX ? parseFloat(refX) : 0.0;
            tfx.elements[13] = refY ? parseFloat(refY) : 0.0;
            tfx.elements[14] = refZ ? parseFloat(refZ) : 0.0;
        }

        let itfx = msTfx.clone();
        itfx.multiply(tfx);
        itfx.invert();
        const cameraD = {
            label: msCamera.getAttribute("label") || '',
            transform: tfx,
            worldToCamera: itfx,
            sensorId: msCamera.getAttribute("sensor_id") ? Number(msCamera.getAttribute("sensor_id")) : 0.0,
            componentId: msCamera.getAttribute("component_id") ? Number(msCamera.getAttribute("component_id")) : 0.0,
            hasReference: reference ? true : false,
            orientation: Number(msCamera.getElementsByTagName('orientation')[0]?.textContent) || 1,
        };
        msCameraData.push(cameraD);
    }

    let sensors: ttMetashapeSensor[] = [];
    const sensorsParent = xml.getElementsByTagName('sensors')[0];
    const msSensors = sensorsParent ? sensorsParent.children : [];
    if (!sensorsParent || msSensors.length === 0) {
        throw new Error(`Invalid Metashape XML ${url}: no sensors found.`);
    }
    for (let msSensorData of msSensors as any) {
        const resolution = msSensorData.getElementsByTagName('resolution')[0] || null;
        let sensor = new ttMetashapeSensor;
        sensor.id = msSensorData.getAttribute('id') ? Number(msSensorData.getAttribute('id')) : 0.0;
        sensor.label = msSensorData.getAttribute('label');

        sensor.widthPx = resolution && resolution.getAttribute('width') ? Number(resolution.getAttribute('width')) : 1.0;
        sensor.heightPx = resolution && resolution.getAttribute('height') ? Number(resolution.getAttribute('height')) : 1.0;

        const calib = msSensorData.getElementsByTagName('calibration')[0] || null;

        const fEl  = calib ? calib.getElementsByTagName('f')[0]  : null;
        const cxEl = calib ? calib.getElementsByTagName('cx')[0] : null;
        const cyEl = calib ? calib.getElementsByTagName('cy')[0] : null;

        sensor.f  = fEl  && fEl.textContent  ? parseFloat(fEl.textContent)  : 1.0;
        sensor.cx = cxEl && cxEl.textContent ? parseFloat(cxEl.textContent) : 0.0;
        sensor.cy = cyEl && cyEl.textContent ? parseFloat(cyEl.textContent) : 0.0;

        sensor.k1 = 0.0;
        sensor.k2 = 0.0;
        sensor.k3 = 0.0;
        sensor.k4 = 0.0;

        const k1El = calib ? calib.getElementsByTagName('k1')[0] : null;
        const k2El = calib ? calib.getElementsByTagName('k2')[0] : null;
        const k3El = calib ? calib.getElementsByTagName('k3')[0] : null;
        const k4El = calib ? calib.getElementsByTagName('k4')[0] : null;

        if (k1El && k1El.textContent) sensor.k1 = parseFloat(k1El.textContent);
        if (k2El && k2El.textContent) sensor.k2 = parseFloat(k2El.textContent);
        if (k3El && k3El.textContent) sensor.k3 = parseFloat(k3El.textContent);
        if (k4El && k4El.textContent) sensor.k4 = parseFloat(k4El.textContent);

        const p1El = calib ? calib.getElementsByTagName('p1')[0] : null;
        const p2El = calib ? calib.getElementsByTagName('p2')[0] : null;

        sensor.p1 = p1El && p1El.textContent ? parseFloat(p1El.textContent) : 0.0;
        sensor.p2 = p2El && p2El.textContent ? parseFloat(p2El.textContent) : 0.0;

        const sensorProps = msSensorData.getElementsByTagName('property') || [];
        for (let sensorProp of sensorProps as any) {
            const sensorPropName = sensorProp.getAttribute('name');
            const val = sensorProp.getAttribute('value');
            if (sensorPropName && val !== null) {
                const v = convertStr(val);
                if (typeof v === 'number') {
                    (sensor as unknown as Record<string, unknown>)[`prop_${sensorPropName}`] = v;
                }
            }
        }
        sensors.push(sensor);
    }

    if (hasReference && !options.useCalibratedCameraTransforms) {
        msTfx.identity();
    }
    if (sensors.length === 0 || msCameraData.length === 0) {
        throw new Error(`Invalid Metashape XML ${url}: required camera/sensor data is empty.`);
    }
    return new ttMetashapeData("test", msTfx, msScale, msCameraData, sensors);
}

/** Legacy wrapper for callers that still pass a project name. */
export async function ttMetashapeParse(projectName: string, signal?: AbortSignal): Promise<ttMetashapeData> {
    return ttMetashapeParseUrl(`${BASE_ASSET_URL}/models/${encodeURIComponent(projectName)}/cameras.xml`, signal);
}
